<script setup lang="ts">
import { Button } from './button';
import { Input } from './input';
import { Alert, AlertDescription, AlertTitle } from './alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './tabs';
import { type Ref, ref } from 'vue';
import { auth } from './firebase';
import {
  GoogleAuthProvider,
  SAMLAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  createUserWithEmailAndPassword,
  updateProfile,
  type UserCredential,
} from 'firebase/auth';
import { useRoute } from 'vue-router';
import { crossAppActions } from './cross-app-actions.ts';
import { alertModal } from './modals/AlertModal.vue';
import type { ActionCodeSettings } from '@firebase/auth';
import releaseVid from '../tri-frontend/assets/releasevid.webm';
import toolkitLogo from '../tri-frontend/assets/toolkit-logo-only.svg';
import sparkles1 from '../tri-frontend/assets/sparkles1.png';
import sparkles2 from '../tri-frontend/assets/sparkles2.png';

// TODO: 1. Cross-App - Ideally this would be more specific, but how do we make this generic?
const appTitle = 'Toolkit';

const route = useRoute();
const awaitingLogin = ref(false as false | 'google' | 'oldcastle' | 'wieland' | 'password' | 'register');
const loginError = ref('');
const email = ref('');
const password = ref('');
const phase = ref('email' as 'email' | 'auth');

// Registration form fields
const registerName = ref('');
const registerEmail = ref('');
const registerPassword = ref('');
const registerConfirmPassword = ref('');
const registerError = ref('');
const resettingPassword = ref(false);

// Testimonials
const testimonials = ref([
  {
    text: 'Toolkit has streamlined our environmental reporting process. What used to take weeks now takes days.',
    author: 'Sarah Chen',
    title: 'Environmental Manager, GreenTech Industries',
  },
  {
    text: 'The automated calculations and data validation have eliminated errors in our submissions.',
    author: 'Michael Rodriguez',
    title: 'Compliance Director, EcoManufacturing',
  },
  {
    text: 'User-friendly interface and excellent support team. Highly recommend for any facility.',
    author: 'Jennifer Walsh',
    title: 'Environmental Specialist, CleanWater Corp',
  },
]);
const currentTestimonial = ref(0);

// Auto-rotate testimonials every 4 seconds
setInterval(() => {
  currentTestimonial.value = (currentTestimonial.value + 1) % testimonials.value.length;
}, 4000);

function oldcastleAuth() {
  return login('oldcastle', loginError, () => signInWithPopup(auth, new SAMLAuthProvider('saml.oldcastle')));
}

function wielandAuth() {
  return login('wieland', loginError, () => signInWithPopup(auth, new SAMLAuthProvider('saml.wieland')));
}

function googleAuth(type: 'login' | 'register') {
  return login('google', loginError, async () => {
    const userCredentials = await signInWithPopup(auth, new GoogleAuthProvider());

    if (type === 'register') {
      await crossAppActions.users.createDemoTenantIfNecessary();
    }

    return userCredentials;
  });
}

function emailAuth() {
  if (!email.value.trim() || !email.value.includes('@')) {
    return;
  }

  if (phase.value === 'email') {
    const emailLower = email.value.toLowerCase();
    if (/(inwesco\.com|nationalpipe\.com|diamondcp\.biz|oldcastle\.com)/.test(emailLower)) {
      oldcastleAuth();
    } else if (/wieland\.com/.test(emailLower)) {
      wielandAuth();
    } else if (emailLower.includes('@tyler.church') || emailLower.includes('@jhjplc.com')) {
      googleAuth('login');
    } else {
      phase.value = 'auth';
    }

    return;
  }

  if (!password.value.trim()) {
    return;
  }

  return login('password', loginError, () => signInWithEmailAndPassword(auth, email.value, password.value));
}

// Add reactive loading message
const loadingMessage = ref('');

async function login(
  type: 'google' | 'oldcastle' | 'wieland' | 'password' | 'register',
  errorRef: Ref<string>,
  action: () => Promise<UserCredential>,
  canRetry = true,
): Promise<void> {
  try {
    awaitingLogin.value = type;
    errorRef.value = '';

    // Set initial loading message
    loadingMessage.value =
      type === 'google'
        ? 'Connecting to Google...'
        : type === 'oldcastle' || type === 'wieland'
          ? 'Redirecting to company login...'
          : type === 'password'
            ? 'Verifying credentials...'
            : type === 'register'
              ? 'Creating your account...'
              : 'Processing...';

    const userCredentials = await action();
    const idToken = await userCredentials.user.getIdToken();
    await fetch('/api/crossapp/set-session-cookie', {
      method: 'POST',
      body: idToken,
    });

    if (FIREBASE_USING_EMULATORS) {
      await crossAppActions.users.ensureAccessOnTestSite();
      const refreshedIdToken = await userCredentials.user.getIdToken(true);
      await fetch('/api/crossapp/set-session-cookie', {
        method: 'POST',
        body: refreshedIdToken,
      });
    }

    // Update message for data loading phase
    loadingMessage.value = 'Loading your workspace...';
    // TODO: 1. Cross-App - Consider bringing this and the router calls back, if we can figure out how to make this more generic across apps:
    // await Promise.all([facilitiesRefresh(), chemicalsRefresh(), youRefresh()]);

    // Final message
    loadingMessage.value = 'Redirecting...';
    const returnTo = route.query.returnTo;
    if (typeof returnTo === 'string' && returnTo !== '/404' && returnTo !== '/no-access') {
      // await router.replace(returnTo);
      window.location.href = returnTo;
    } else {
      // await router.replace('/');
      window.location.href = '/';
    }
  } catch (e: any) {
    console.error(e);
    const errorCode = e.code;
    if (errorCode === 'auth/popup-closed-by-user') {
      return;
    }

    if (errorCode === 'auth/user-not-found') {
      await alertModal({
        title: 'User Not Found',
        message: 'We could not locate you in the system.',
      });
      return;
    }

    if (
      errorCode === 'auth/internal-error' &&
      typeof e.message === 'string' &&
      e.message.includes('deadline exceeded') &&
      canRetry
    ) {
      console.info(e, 'beforeUserSignedIn likely took too long, trying again.');
      return login(type, errorRef, action, false);
    }

    errorRef.value = `We were unable to log you in. Please check your credentials and try again.`;
    if (errorCode !== 'auth/invalid-credential') {
      const errorMessage = e.message;
      errorRef.value += `\n\nError was: ${errorMessage}`;
    }
  } finally {
    awaitingLogin.value = false;
  }
}

async function passwordReset() {
  if (!email.value) {
    await alertModal({
      title: 'Need Email Address',
      message: 'Please enter your email address before requesting a password reset.',
    });
    return;
  }

  try {
    resettingPassword.value = true;

    let url = window.location.origin;
    const returnTo = route.query.returnTo;
    if (typeof returnTo === 'string' && returnTo !== '/404' && returnTo !== '/no-access' && returnTo.startsWith('/')) {
      url += returnTo;
    }
    const settings: ActionCodeSettings = {
      url,
    };
    if (window.location.host.includes('envirotoolkit.com')) {
      settings.linkDomain = window.location.host;
    }

    await sendPasswordResetEmail(auth, email.value, settings);
    await alertModal({
      title: 'Password Reset Email Sent',
      message: `A password reset email has been sent to ${email.value}. Please check your inbox and follow the instructions.`,
    });
  } catch (error: any) {
    await alertModal({
      title: 'Password Reset Failed',
      message: `We couldn't send the password reset email. Please try again. Error: ${error.message}`,
    });
  } finally {
    resettingPassword.value = false;
  }
}

async function registerUser() {
  registerError.value = '';

  if (registerPassword.value !== registerConfirmPassword.value) {
    registerError.value = 'Passwords do not match';
    return;
  }

  return login('register', registerError, async () => {
    const userCredential = await createUserWithEmailAndPassword(auth, registerEmail.value, registerPassword.value);

    await updateProfile(userCredential.user, {
      displayName: registerName.value,
    });

    await crossAppActions.users.createDemoTenantIfNecessary();

    await crossAppActions.users.youUpdate({ name: registerName.value });

    return userCredential;
  });
}
</script>

<template>
  <div class="bg-brand-blue flex min-h-screen w-screen overflow-hidden p-2 sm:p-4">
    <!-- Left Side - Image with Content (Desktop Only) -->
    <div
      class="relative hidden h-[calc(100vh-2rem)] w-1/2 flex-col justify-between overflow-hidden rounded-l-2xl lg:flex"
    >
      <!-- Background GIF -->
      <video
        :src="releaseVid"
        autoplay
        loop
        playsinline
        muted
        disablepictureinpicture
        disableremoteplayback
        class="absolute inset-0 h-full w-full rounded-l-2xl object-cover"
      />
      <!-- Purple overlay -->
      <div
        class="from-brand-blue/80 via-brand-blue/70 to-brand-blue/90 absolute inset-0 rounded-l-2xl bg-linear-to-br"
      ></div>

      <!-- Top left logo -->
      <div class="relative z-10 p-8">
        <div class="flex items-center text-white">
          <div class="mr-4 rounded-lg bg-white/20 p-3 backdrop-blur-xs">
            <img :src="toolkitLogo" alt="TRI Toolkit Logo" class="h-10 w-10" />
          </div>
          <span class="text-2xl font-bold">{{ appTitle }}</span>
        </div>
      </div>

      <!-- Center content -->
      <div class="relative z-10 px-8 pb-8">
        <div class="mb-8 flex min-h-[120px] items-center text-4xl leading-tight font-bold text-white md:text-5xl">
          <p>"{{ testimonials[currentTestimonial].text }}"</p>
        </div>

        <!-- Carousel indicators -->
        <div class="mt-8 flex space-x-2">
          <div
            v-for="(_, index) in testimonials"
            :key="index"
            :class="[
              'h-1 rounded-full transition-all duration-300',
              index === currentTestimonial ? 'w-8 bg-white' : 'w-3 bg-white/40',
            ]"
          ></div>
        </div>
      </div>
    </div>

    <!-- Mobile Background (Behind Form) -->
    <div class="absolute inset-0 lg:hidden">
      <!-- Background GIF -->
      <video
        :src="releaseVid"
        autoplay
        loop
        playsinline
        muted
        disablepictureinpicture
        disableremoteplayback
        class="h-full w-full object-cover"
      />
      <!-- Purple overlay - balanced opacity to show GIF subtly -->
      <div class="from-brand-blue/85 via-brand-blue/80 to-brand-blue/90 absolute inset-0 bg-linear-to-br"></div>
    </div>

    <!-- Right Side - Form -->
    <div
      class="relative flex h-[calc(100vh-1rem)] w-full items-center justify-center overflow-hidden rounded-2xl lg:h-[calc(100vh-2rem)] lg:w-1/2 lg:rounded-l-none lg:rounded-r-2xl"
    >
      <!-- Background pattern -->
      <div
        class="from-brand-blue to-brand-blue/90 absolute inset-0 rounded-2xl bg-linear-to-br opacity-20 lg:rounded-l-none lg:rounded-r-2xl"
      ></div>

      <!-- Mobile Logo (Top Center) - Hidden, will be moved to form header -->
      <div class="absolute top-8 left-1/2 z-30 hidden -translate-x-1/2">
        <div class="flex items-center text-white">
          <div class="mr-3 rounded-lg bg-white/20 p-2 backdrop-blur-xs">
            <img :src="toolkitLogo" alt="TRI Toolkit Logo" class="h-8 w-8" />
          </div>
          <span class="text-xl font-bold">{{ appTitle }}</span>
        </div>
      </div>

      <!-- Top right menu -->
      <div class="absolute top-8 right-4 z-30 lg:right-8">
        <a
          href="https://tritoolkit.com"
          target="_blank"
          class="group flex items-center space-x-2 text-sm font-medium text-white/80 transition-all duration-300 hover:scale-105 hover:text-white"
        >
          <span class="relative">
            Learn More
            <span
              class="absolute bottom-0 left-0 h-px w-0 bg-emerald-400 transition-all duration-300 group-hover:w-full"
            ></span>
          </span>
          <i
            class="fas fa-external-link-alt text-xs opacity-60 transition-opacity duration-300 group-hover:opacity-100"
          ></i>
        </a>
      </div>

      <!-- Form Container -->
      <div
        class="bg-brand-blue relative z-10 w-full max-w-md overflow-hidden rounded-2xl border border-white/10 px-6 py-6 backdrop-blur-xs lg:bg-white/5 lg:px-8 lg:py-8"
        :class="{ 'pointer-events-none': awaitingLogin }"
      >
        <!-- Loading Overlay -->
        <div
          v-if="awaitingLogin"
          class="animate-fade-in absolute inset-0 z-50 flex items-center justify-center rounded-2xl bg-black/20 backdrop-blur-xs"
        >
          <div class="text-center">
            <div class="mb-4 flex justify-center">
              <div class="h-8 w-8 animate-spin rounded-full border-2 border-white/30 border-t-white"></div>
            </div>
            <p class="text-sm font-medium text-white">
              {{ loadingMessage }}
            </p>
            <p class="mt-2 text-xs text-white/60">Please wait</p>
            <!-- Progress dots animation -->
            <div class="mt-3 flex justify-center space-x-1">
              <div class="h-2 w-2 animate-pulse rounded-full bg-white/40"></div>
              <div class="h-2 w-2 animate-pulse rounded-full bg-white/40" style="animation-delay: 0.2s"></div>
              <div class="h-2 w-2 animate-pulse rounded-full bg-white/40" style="animation-delay: 0.4s"></div>
            </div>
          </div>
        </div>
        <!-- Large background sparkles -->
        <img :src="sparkles1" alt="Sparkle" class="pointer-events-none absolute top-4 right-4 h-24 w-24 opacity-20" />
        <img :src="sparkles2" alt="Sparkle" class="pointer-events-none absolute top-4 left-4 h-24 w-24 opacity-20" />

        <!-- Header -->
        <div class="relative z-10 mb-6 text-center lg:mb-8">
          <!-- Desktop: Show Welcome -->
          <div class="mb-2 hidden items-center justify-center lg:flex">
            <h2 class="text-2xl font-bold text-white lg:text-3xl">Welcome</h2>
          </div>

          <!-- Mobile: Show TRI Toolkit Logo and Title -->
          <div class="mb-2 flex items-center justify-center lg:hidden">
            <div class="mr-3 rounded-lg bg-white/20 p-2 backdrop-blur-xs">
              <img :src="toolkitLogo" alt="TRI Toolkit Logo" class="h-8 w-8" />
            </div>
            <h2 class="text-xl font-bold text-white">{{ appTitle }}</h2>
          </div>

          <p class="text-sm text-gray-400 lg:text-base">Sign in or create an account</p>
        </div>

        <!-- Tabs -->
        <Tabs defaultValue="login" class="w-full">
          <TabsList class="grid w-full grid-cols-2 border-white/20 bg-white/10">
            <TabsTrigger
              value="login"
              class="data-[state=active]:text-brand-blue text-white/80 data-[state=active]:bg-white"
              >Sign In</TabsTrigger
            >
            <TabsTrigger
              value="register"
              class="data-[state=active]:text-brand-blue text-white/80 data-[state=active]:bg-white"
              >Register</TabsTrigger
            >
          </TabsList>

          <!-- Register Tab -->
          <TabsContent value="register" class="mt-4 space-y-4">
            <form @submit.prevent="registerUser()">
              <Alert
                v-if="registerError"
                variant="destructive"
                class="mb-4 border-red-500/20 bg-red-900/20 text-red-400"
              >
                <AlertTitle>Registration Error</AlertTitle>
                <AlertDescription>
                  {{ registerError }}
                </AlertDescription>
              </Alert>

              <div class="space-y-4">
                <!-- Name fields row -->
                <div class="relative">
                  <i class="fas fa-user absolute top-1/2 left-3 -translate-y-1/2 transform text-white/60"></i>
                  <Input
                    id="registerName"
                    type="text"
                    placeholder="Name"
                    :disabled="awaitingLogin"
                    required
                    v-model.trim="registerName"
                    class="border-white/20 bg-white/10 pl-10 text-white placeholder:text-white/60 focus:border-white focus:ring-white/20"
                  />
                </div>

                <!-- Email -->
                <div class="relative">
                  <i class="fas fa-envelope absolute top-1/2 left-3 -translate-y-1/2 transform text-white/60"></i>
                  <Input
                    id="registerEmail"
                    type="email"
                    placeholder="Email"
                    :disabled="awaitingLogin"
                    required
                    v-model.trim="registerEmail"
                    class="border-white/20 bg-white/10 pl-10 text-white placeholder:text-white/60 focus:border-white focus:ring-white/20"
                  />
                </div>

                <!-- Password -->
                <div class="relative">
                  <i class="fas fa-lock absolute top-1/2 left-3 -translate-y-1/2 transform text-white/60"></i>
                  <Input
                    id="registerPassword"
                    type="password"
                    placeholder="Enter your password"
                    :disabled="awaitingLogin"
                    required
                    minlength="8"
                    v-model="registerPassword"
                    class="border-white/20 bg-white/10 pr-10 pl-10 text-white placeholder:text-white/60 focus:border-white focus:ring-white/20"
                  />
                </div>

                <!-- Confirm Password -->
                <div class="relative">
                  <i class="fas fa-lock absolute top-1/2 left-3 -translate-y-1/2 transform text-white/60"></i>
                  <Input
                    id="registerConfirmPassword"
                    type="password"
                    placeholder="Confirm your password"
                    :disabled="awaitingLogin"
                    required
                    minlength="8"
                    v-model="registerConfirmPassword"
                    class="border-white/20 bg-white/10 pr-10 pl-10 text-white placeholder:text-white/60 focus:border-white focus:ring-white/20"
                  />
                </div>

                <!-- Terms checkbox -->
                <div class="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="terms"
                    class="mt-1 rounded-sm border-white/20 bg-white/10 text-white focus:ring-white/20"
                    required
                  />
                  <label for="terms" class="text-sm text-white/80">
                    I agree to the
                    <a href="/terms.html" target="_blank" class="text-white underline hover:text-white/80">
                      Terms & Conditions
                    </a>
                  </label>
                </div>

                <!-- Create Account Button -->
                <Button
                  type="submit"
                  class="w-full rounded-lg bg-emerald-600 py-3 font-medium text-white shadow-lg transition-all duration-200 hover:bg-emerald-700 hover:shadow-xl"
                  :disabled="awaitingLogin"
                  :class="{ 'animate-pulse': awaitingLogin === 'register' }"
                >
                  <div class="flex items-center justify-center">
                    <i v-if="awaitingLogin === 'register'" class="fa fa-spin fa-spinner-third mr-2"></i>
                    <span>
                      {{ awaitingLogin === 'register' ? 'Creating account...' : 'Create account' }}
                    </span>
                  </div>
                </Button>

                <!-- Divider -->
                <div class="relative my-6">
                  <div class="absolute inset-0 flex items-center">
                    <div class="w-full border-t border-white/20"></div>
                  </div>
                  <div class="relative flex justify-center text-sm">
                    <span class="bg-brand-blue px-2 text-white/60">Or register with</span>
                  </div>
                </div>

                <!-- Social Login Buttons -->
                <div class="w-full">
                  <Button
                    type="button"
                    variant="outline"
                    @click="googleAuth('register')"
                    :disabled="awaitingLogin"
                    class="w-full border-white/20 bg-white/10 py-3 text-white transition-all duration-200 hover:bg-white/20 hover:text-white"
                    :class="{ 'animate-pulse border-emerald-500/30 bg-emerald-900/20': awaitingLogin === 'google' }"
                  >
                    <div class="flex items-center justify-center">
                      <i
                        :class="awaitingLogin === 'google' ? 'fa fa-spin fa-spinner-third mr-2' : 'fab fa-google mr-2'"
                      ></i>
                      <span>
                        {{ awaitingLogin === 'google' ? 'Connecting to Google...' : 'Google' }}
                      </span>
                    </div>
                  </Button>
                </div>
              </div>
            </form>
          </TabsContent>

          <!-- Login Tab -->
          <TabsContent value="login" class="mt-4 space-y-4">
            <form @submit.prevent="emailAuth()">
              <Alert v-if="loginError" variant="destructive" class="mb-4 border-red-500/20 bg-red-900/20 text-red-400">
                <AlertTitle>Login Error</AlertTitle>
                <AlertDescription>
                  {{ loginError }}
                </AlertDescription>
              </Alert>

              <div class="space-y-4">
                <!-- Email -->
                <div class="relative">
                  <i class="fas fa-envelope absolute top-1/2 left-3 -translate-y-1/2 transform text-white/60"></i>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Email"
                    :disabled="awaitingLogin || phase === 'auth'"
                    required
                    v-model="email"
                    class="border-white/20 bg-white/10 pl-10 text-white placeholder:text-white/60 focus:border-white focus:ring-white/20"
                  />
                </div>

                <!-- Password (shown in auth phase) -->
                <div v-if="phase === 'auth'">
                  <div class="relative">
                    <i class="fas fa-lock absolute top-1/2 left-3 -translate-y-1/2 transform text-white/60"></i>
                    <Input
                      id="password"
                      type="password"
                      placeholder="Enter your password"
                      :disabled="awaitingLogin"
                      required
                      v-model="password"
                      class="border-white/20 bg-white/10 pr-10 pl-10 text-white placeholder:text-white/60 focus:border-white focus:ring-white/20"
                    />
                    <button
                      type="button"
                      class="absolute top-1/2 right-3 -translate-y-1/2 transform text-white/60 hover:text-white"
                    >
                      <i class="fas fa-eye"></i>
                    </button>
                  </div>
                </div>

                <!-- Login Button -->
                <Button
                  type="submit"
                  class="w-full rounded-lg bg-emerald-600 py-3 font-medium text-white shadow-lg transition-all duration-200 hover:bg-emerald-700 hover:shadow-xl"
                  :disabled="awaitingLogin"
                  :class="{ 'animate-pulse': awaitingLogin === 'password' }"
                >
                  <div class="flex items-center justify-center">
                    <i v-if="awaitingLogin === 'password'" class="fa fa-spin fa-spinner-third mr-2"></i>
                    <span>
                      {{ awaitingLogin === 'password' ? 'Authenticating...' : phase === 'email' ? 'Proceed' : 'Login' }}
                    </span>
                  </div>
                </Button>

                <template v-if="phase === 'auth'">
                  <!-- Reset Password -->
                  <div class="text-center">
                    <a
                      href="#"
                      @click.prevent="passwordReset"
                      class="text-sm text-white underline transition-colors duration-200 hover:text-white/80"
                      :class="{ 'pointer-events-none opacity-50': resettingPassword }"
                    >
                      <i v-if="resettingPassword" class="fa fa-spin fa-spinner-third mr-1"></i>
                      {{ resettingPassword ? 'Sending reset email...' : 'Reset password?' }}
                    </a>
                  </div>

                  <div class="relative my-6">
                    <div class="absolute inset-0 flex items-center">
                      <div class="w-full border-t border-white/20"></div>
                    </div>
                    <div class="relative flex justify-center text-sm">
                      <span class="bg-brand-blue px-2 text-white/60">Or continue with</span>
                    </div>
                  </div>

                  <!-- Google Auth (shown in auth phase) -->
                  <Button
                    type="button"
                    variant="outline"
                    @click="googleAuth('login')"
                    :disabled="awaitingLogin"
                    class="w-full border-white/20 bg-white/10 py-3 text-white transition-all duration-200 hover:bg-white/20 hover:text-white"
                    :class="{ 'animate-pulse border-emerald-500/30 bg-emerald-900/20': awaitingLogin === 'google' }"
                  >
                    <div class="flex items-center justify-center">
                      <i
                        :class="awaitingLogin === 'google' ? 'fa fa-spin fa-spinner-third mr-2' : 'fab fa-google mr-2'"
                      ></i>
                      <span>
                        {{ awaitingLogin === 'google' ? 'Connecting to Google...' : 'Google' }}
                      </span>
                    </div>
                  </Button>
                </template>
              </div>
            </form>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Custom animations for sparkles */
@keyframes spin-slow {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.animate-spin-slow {
  animation: spin-slow 4s linear infinite;
}

/* Enhanced pulse animation */
@keyframes pulse-gentle {
  0%,
  100% {
    opacity: 0.4;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.1);
  }
}

.animate-pulse {
  animation: pulse-gentle 3s ease-in-out infinite;
}

/* Floating animation for special elements */
@keyframes float {
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
  100% {
    transform: translateY(0px);
  }
}

.animate-float {
  animation: float 3s ease-in-out infinite;
}

/* Backdrop blur support */
.backdrop-blur-xs {
  backdrop-filter: blur(4px);
}

/* Gradient overlay for better text readability */
.bg-gradient-overlay {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
}

/* Fade in animation for loading overlay */
@keyframes fade-in {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.animate-fade-in {
  animation: fade-in 0.2s ease-out;
}

/* Enhanced pulse animation for progress dots */
@keyframes pulse-dots {
  0%,
  70%,
  100% {
    transform: scale(0.8);
    opacity: 0.4;
  }
  35% {
    transform: scale(1);
    opacity: 1;
  }
}
</style>
